import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import type { Project } from '../../types/project'
import { formatDate } from '../../utils/date'

interface ProjectSelectorItemProps {
  project: Project
  onClick: () => void
}

const ProjectSelectorItem: React.FC<ProjectSelectorItemProps> = ({
  project,
  onClick,
}) => {
  const getTruckTypesDisplay = () => {
    if (!project.truck_types || project.truck_types.length === 0) {
      return null
    }
    return project.truck_types.join(', ')
  }

  // Helper to format user display with company name and address
  const formatUserDisplay = (user: {
    first_name?: string
    last_name?: string
    email?: string
    company?: { name?: string; address?: string } | null
  }) => {
    const name =
      `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
    const companyName = user.company?.name
    const companyAddress = user.company?.address

    if (companyName && companyAddress) {
      return `${name} (${companyName}, ${companyAddress})`
    } else if (companyName) {
      return `${name} (${companyName})`
    }
    return name || ''
  }

  // Get involved parties from snapshot data
  const getInvolvedParties = () => {
    const parties: { role: string; users: string[] }[] = []

    // Contractors (from snapshot via resolve_contractors)
    if (project.contractors && project.contractors.length > 0) {
      parties.push({
        role: 'Contractor',
        users: project.contractors.map(formatUserDisplay).filter(Boolean),
      })
    }

    // Brokers (from snapshot via resolve_brokers)
    if (project.brokers && project.brokers.length > 0) {
      parties.push({
        role: 'Broker',
        users: project.brokers.map(formatUserDisplay).filter(Boolean),
      })
    }

    // Sub Haulers (from snapshot via resolve_sub_haulers)
    if (project.sub_haulers && project.sub_haulers.length > 0) {
      parties.push({
        role: 'Sub Hauler',
        users: project.sub_haulers.map(formatUserDisplay).filter(Boolean),
      })
    }

    // Third Party Contractors (from snapshot via resolve_third_party_contractors)
    if (
      project.third_party_contractors &&
      project.third_party_contractors.length > 0
    ) {
      parties.push({
        role: 'Third Party',
        users: project.third_party_contractors
          .map(formatUserDisplay)
          .filter(Boolean),
      })
    }

    // Third Party Brokers (from snapshot via resolve_third_party_brokers)
    if (project.third_party_brokers && project.third_party_brokers.length > 0) {
      parties.push({
        role: 'Third Party Broker',
        users: project.third_party_brokers
          .map(formatUserDisplay)
          .filter(Boolean),
      })
    }

    return parties
  }

  const involvedParties = getInvolvedParties()

  return (
    <button type="button" className={css(styles.itemButton)} onClick={onClick}>
      <div className={css(styles.mainInfo)}>
        {/* Header Row: Label + Job Site */}
        <div className={css(styles.headerRow)}>
          <span className={css(styles.label)}>{project.label}</span>
          {project.job_site && (
            <span className={css(styles.jobSite)}>{project.job_site}</span>
          )}
        </div>

        {/* IDs Row: PO Number + Job ID */}
        {(project.po_number || project.job_id) && (
          <div className={css(styles.idsRow)}>
            {project.po_number && (
              <span className={css(styles.idTag)}>
                <span className={css(styles.idLabel)}>PO:</span>{' '}
                {project.po_number}
              </span>
            )}
            {project.job_id && (
              <span className={css(styles.idTag)}>
                <span className={css(styles.idLabel)}>Job ID:</span>{' '}
                {project.job_id}
              </span>
            )}
          </div>
        )}

        {/* Locations Row */}
        {(project.pickup_address || project.dropoff_address) && (
          <div className={css(styles.locationsRow)}>
            {project.pickup_address && (
              <div className={css(styles.location)}>
                <span className={css(styles.locationLabel)}>Pickup:</span>
                <span className={css(styles.locationText)}>
                  {project.pickup_address}
                </span>
              </div>
            )}
            {project.dropoff_address && (
              <div className={css(styles.location)}>
                <span className={css(styles.locationLabel)}>Dropoff:</span>
                <span className={css(styles.locationText)}>
                  {project.dropoff_address}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Involved Parties Row */}
        {involvedParties.length > 0 && (
          <div className={css(styles.partiesRow)}>
            {involvedParties.map((party) => (
              <div key={party.role} className={css(styles.partyGroup)}>
                <span className={css(styles.partyRole)}>{party.role}:</span>
                <span className={css(styles.partyUsers)}>
                  {party.users.join(', ')}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Details Row: Material, Date, Trucks */}
        <div className={css(styles.detailsRow)}>
          {project.material && (
            <span className={css(styles.tag)}>
              <i className={`fa-solid fa-box ${css(styles.tagIcon)}`} />
              {project.material}
            </span>
          )}

          {project.starting_date && (
            <span className={css(styles.tag)}>
              <i className={`fa-solid fa-calendar ${css(styles.tagIcon)}`} />
              {formatDate(project.starting_date)}
            </span>
          )}

          {project.trucks_number && (
            <span className={css(styles.tag)}>
              <i className={`fa-solid fa-truck ${css(styles.tagIcon)}`} />
              {project.trucks_number} truck
              {Number(project.trucks_number) !== 1 ? 's' : ''}
            </span>
          )}

          {getTruckTypesDisplay() && (
            <span className={css(styles.tag, styles.truckTypesTag)}>
              {getTruckTypesDisplay()}
            </span>
          )}
        </div>
      </div>

      <div className={css(styles.arrowContainer)}>
        <i className={`fa-solid fa-chevron-right ${css(styles.arrow)}`} />
      </div>
    </button>
  )
}

const styles = StyleSheet.create({
  itemButton: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    padding: '16px',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    ':hover': {
      borderColor: '#3B82F6',
      backgroundColor: '#F0F7FF',
    },
  },
  mainInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  label: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#111827',
  },
  jobSite: {
    fontSize: '14px',
    color: '#374151',
    fontWeight: 500,
  },
  idsRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  idTag: {
    fontSize: '12px',
    color: '#6B7280',
  },
  idLabel: {
    fontWeight: 500,
    color: '#9CA3AF',
  },
  locationsRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  location: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#6B7280',
  },
  locationLabel: {
    fontWeight: 500,
    color: '#9CA3AF',
    flexShrink: 0,
  },
  locationText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '400px',
  },
  partiesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 16px',
    padding: '8px 0',
    borderTop: '1px solid #F3F4F6',
    borderBottom: '1px solid #F3F4F6',
  },
  partyGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
  },
  partyRole: {
    color: '#9CA3AF',
    fontWeight: 500,
  },
  partyUsers: {
    color: '#374151',
  },
  detailsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    backgroundColor: '#F3F4F6',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#374151',
  },
  tagIcon: {
    fontSize: '10px',
    color: '#6B7280',
  },
  truckTypesTag: {
    backgroundColor: '#EFF6FF',
    color: '#1D4ED8',
  },
  arrowContainer: {
    marginLeft: '16px',
    marginTop: '4px',
    flexShrink: 0,
  },
  arrow: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
})

export default ProjectSelectorItem
